import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import connect from '@app/utils/connect';
import sendNotification from '@app/actions/notifications/send';
import combineActions from '@app/utils/combineActions';
import ReactDOM from 'react-dom';
import * as he from 'he';
import { Button, Typography } from '@app/ui';
import { CloseOutlined } from '@ant-design/icons';
import './DownloadModal.scss';
const { Text } = Typography;
import DownloadPostDragTable from './DownloadPostDragTable';
import createPDF from '@app/utils/createPDF';
import { toSportsCdnUrl } from '@app/utils/sportsCdnUrl';
declare let __PROD_API__;

const notify = (msg: string, context: 'success' | 'warning' | 'danger') =>
    sendNotification()(undefined, msg, { context });

type Post = {
    id: number;
    mediaUrl: string;
    content: string;
    socialAccount: {
        username: string;
    };
    socialPlatform: string;
    cdnMediaUrl: string;
    createdAt: string;
    sponsorAnnotationsDetails: any;
};

export type DownloadModalProps = {
    visible: boolean;
    onClose: () => void;
    title: string;
    posts: Record<number, Post>;
    user: any;
    filters: any;
    client: any;
    primaryColor: string;
    secondaryColor: string;
    onFinishDownload: () => void;
};

const DownloadModal: React.FC<DownloadModalProps> = ({
    visible,
    onClose,
    title,
    posts,
    user,
    filters,
    client,
    primaryColor,
    secondaryColor,
    onFinishDownload,
}) => {
    const { t } = useTranslation('measurement');
    const [isDownloadingReport, setDownloadingReport] = useState(false);
    const [downloadablePosts, setPosts] = useState(() =>
        Object.values(posts).reduce((acc, post) => {
            const hasAccountUsername = post.socialAccount.username;
            acc.push({
                ...post,
                username: hasAccountUsername ? post.socialAccount.username : '',
            });
            return acc;
        }, []),
    );

    const modalRoot = useMemo(() => {
        const existing = document.getElementById('assign-collections-modal');
        if (existing) return existing;
        const div = document.createElement('div');
        div.id = 'assign-collections-modal';
        document.body.appendChild(div);
        return div;
    }, []);

    if (!visible) return null;

    const _download = async () => {
        try {
            setDownloadingReport(true);

            const env = __PROD_API__ ? 'production' : 'development';
            const { token } = user;
            const currency = client.currency.code;

            const reqBody = {
                posts: downloadablePosts.map(post => {
                    const annotationsWithRHLogos = (
                        post.sponsorAnnotationsDetails || []
                    ).map(annotation => {
                        const rh = annotation.rightsholder;
                        const rhId = rh?.id;
                        const logos =
                            filters.rightsholders.list?.[rhId]
                                ?.rightsholderLogos || [];
                        const logoUrl =
                            logos.length > 0
                                ? toSportsCdnUrl(logos[0].url)
                                : null;

                        return {
                            ...annotation,
                            rightsholder: {
                                ...rh,
                                url: logoUrl,
                            },
                        };
                    });

                    return {
                        imgUrl: post.cdnMediaUrl,
                        imgHeight: post.height,
                        imgWidth: post.width,
                        username: post.socialAccount.username
                            ? `@${post.socialAccount.username}`
                            : '',
                        caption: post.content
                            ? `"${he.decode(post.content)}"`
                            : '',
                        postUrl: post.postUrl,
                        annotations: annotationsWithRHLogos,
                        impressions: post.impressions,
                        videoViews: post.videoViews,
                        engagements: post.engagements,
                        fullMediaValue: post.fullMediaValue,
                        totalSponsorMediaValue: post.totalSponsorMediaValue,
                        currency,
                        platform: post.socialPlatform.toLowerCase(),
                        showRightsholders: true,
                        postId: post.id,
                        token,
                        env,
                        hasPaidMetrics: post.hasPaidMetrics,
                        paidEngagements: post.paidEngagements,
                        paidFullMediaValue: post.paidFullMediaValue,
                        paidImpressions: post.paidImpressions,
                        paidVideoViews: post.paidVideoViews,
                        organicEngagements: post.organicEngagements,
                        organicFullMediaValue: post.organicFullMediaValue,
                        organicImpressions: post.organicImpressions,
                        organicVideoViews: post.organicVideoViews,
                    };
                }),
                options: {
                    primaryColor,
                    secondaryColor,
                },
            };

            await createPDF({
                reqBody,
                type: 'post-pdf',
                document,
                filename: Date.now(),
                notif: () => {
                    notify(
                        t(
                            'socialMediaExposure.notifications.reportPdfDownloading',
                        ),
                        'success',
                    );
                },
            });
        } catch (err) {
            console.error(err);
        } finally {
            setDownloadingReport(false);
            onFinishDownload();
        }
    };

    return ReactDOM.createPortal(
        <div className="download-modal-overlay-v2">
            <div className="download-modal-box-v2">
                <Button
                    type="text"
                    shape="circle"
                    icon={<CloseOutlined />}
                    onClick={onClose}
                    className="download-modal-close-v2"
                />

                <Text strong>
                    {`${title} - (${downloadablePosts.length}) ${t('socialMediaExposure.modals.selected')}`}
                </Text>

                <DownloadPostDragTable
                    posts={downloadablePosts}
                    setPosts={setPosts}
                />
                <div className="download-modal-actions-v2">
                    <Button
                        onClick={onClose}
                        color="default"
                        variant="solid"
                        className="action-button"
                        icon={<CloseOutlined />}
                        style={{ fontFamily: 'pnregular' }}
                    >
                        {t('socialMediaExposure.actions.cancel')}
                    </Button>
                    <Button
                        type="primary"
                        onClick={_download}
                        className="action-button"
                        icon={<i className="fas fa-download" />}
                        style={{ fontFamily: 'pnregular' }}
                        loading={isDownloadingReport}
                    >
                        {t('socialMediaExposure.actions.downloadPdfs')}
                    </Button>
                </div>
            </div>
        </div>,
        modalRoot,
    );
};

export default connect(
    ['filters', 'user', 'client'],
    combineActions({
        sendNotification,
    }),
)(DownloadModal);
